import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface DriverPriceChange {
  date: string;
  driver: string;
  value: string | null;
}

interface DriverPriceChangesFormProps {
  onSave?: (data: DriverPriceChange[]) => void;
  onCancel?: () => void;
  initialData?: { date: string; changes: Record<string, string | null> };
}

const DRIVERS = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];

export const DriverPriceChangesForm: React.FC<DriverPriceChangesFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const { user } = useAuth();

  // Match the Friday-only date-picker pattern from DriversForm.tsx so the
  // commentary surface is consistent. All math is in local time — never
  // `new Date(dateStr)` which would interpret it as UTC midnight.
  const getNextFriday = (date: Date = new Date()): string => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const localDate = new Date(year, month, day);
    const currentDay = localDate.getDay();
    const daysUntilFriday = currentDay <= 5 ? 5 - currentDay : 7 - currentDay + 5;
    localDate.setDate(day + daysUntilFriday);
    const y = localDate.getFullYear();
    const m = String(localDate.getMonth() + 1).padStart(2, '0');
    const d = String(localDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getAllFridays = (): string[] => {
    const fridays: string[] = [];
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    const sevenWeeksAhead = new Date(today);
    sevenWeeksAhead.setDate(today.getDate() + (7 * 7));
    const currentDate = new Date(oneWeekAgo);
    while (currentDate <= sevenWeeksAhead) {
      if (currentDate.getDay() === 5) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        fridays.push(`${year}-${month}-${day}`);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return fridays;
  };

  const getDefaultDate = (): string => getNextFriday();

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const initializeChanges = (): Record<string, string | null> => {
    const changes: Record<string, string | null> = {};
    DRIVERS.forEach(driver => {
      changes[driver] = initialData?.changes?.[driver] || null;
    });
    return changes;
  };

  const [selectedDate, setSelectedDate] = useState<string>(
    initialData?.date || getDefaultDate()
  );
  const [driverChanges, setDriverChanges] = useState<Record<string, string | null>>(
    initializeChanges()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setSelectedDate(initialData.date);
      setDriverChanges(initializeChanges());
    }
  }, [initialData]);

  const handleDriverChange = (driver: string, value: string) => {
    setDriverChanges(prev => ({
      ...prev,
      [driver]: value || null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const changes: DriverPriceChange[] = DRIVERS.map(driver => ({
        date: selectedDate,
        driver,
        value: driverChanges[driver] || null,
      }));

      if (onSave) {
        await onSave(changes);
        setSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="bg-rs-jet p-6 rounded-lg">
      <h2 className="text-2xl font-bold text-rs-green mb-6">
        Driver Price Changes - {formatDate(selectedDate)}
      </h2>

      {error && (
        <div className="bg-rs-red/20 border border-rs-red text-rs-red px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-rs-green/20 border border-rs-green text-rs-green px-4 py-3 rounded mb-4">
          Changes saved successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="max-w-xs">
          <label className="block text-sm font-medium text-rs-off-white mb-2">
            Date
          </label>
          {initialData ? (
            <input
              ref={(input) => {
                if (input) {
                  input.addEventListener('click', (e) => {
                    e.preventDefault();
                    try {
                      (input as any).showPicker?.();
                    } catch {
                      input.focus();
                    }
                  });
                }
              }}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-rs-black border border-rs-jet-light rounded text-rs-off-white focus:outline-none focus:border-rs-green cursor-pointer"
              style={{
                colorScheme: 'dark'
              }}
            />
          ) : (
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-rs-black border border-rs-jet-light rounded text-rs-off-white focus:outline-none focus:border-rs-green cursor-pointer"
            >
              {getAllFridays().map((friday) => (
                <option key={friday} value={friday}>{formatDate(friday)}</option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DRIVERS.map((driver) => (
            <div key={driver}>
              <label className="block text-sm font-medium text-rs-off-white mb-2">
                {driver}
              </label>
              <input
                type="text"
                value={driverChanges[driver] || ''}
                onChange={(e) => handleDriverChange(driver, e.target.value)}
                placeholder="e.g., Flat, -2, +3"
                className="w-full px-3 py-2 bg-rs-black border border-rs-jet-light rounded text-rs-off-white focus:outline-none focus:border-rs-green"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-rs-jet-light text-rs-off-white rounded hover:bg-rs-jet transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-rs-green !text-rs-black rounded hover:bg-rs-dark-green transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DriverPriceChangesForm;

